/**
 * Normalize TipTap paragraph spacing for outgoing email HTML.
 *
 * The in-app composer and preview render `<p>` with **zero margin**: the app
 * loads Tailwind Preflight (`@tailwind base` in `src/index.css`), which resets
 * `p { margin: 0 }`, and the `prose` classes on the editor are inert (the
 * `@tailwindcss/typography` plugin is not installed). So the sender sees
 * paragraphs sitting tight, with blank lines only where there is an explicit
 * empty `<p></p>`.
 *
 * The Outlook send path already replicates this with an inline `margin:0` on
 * every `<p>` (plus `&nbsp;` to give empty paragraphs height). The Gmail path
 * historically did **not**, so Gmail (and other mail clients) applied their own
 * default `p { margin: ~1em 0 }`, opening a blank line between every paragraph
 * that the sender never saw in the composer.
 *
 * Applying this transform to the Gmail body makes Gmail render identically to
 * the composer/preview and to Outlook. The regex matches only **bare** `<p>`
 * tags, so already-styled paragraphs (e.g. the compliance footer's
 * `<p style="...">`) are left untouched, and the transform is idempotent.
 *
 * This is intentionally identical to the Outlook transform in
 * `useEmailSender.ts` / `email-queue-processor` `sendViaOutlook`. It is kept as
 * a separate, Gmail-only helper so the working Outlook path is not modified.
 */
export function normalizeEmailParagraphs(html: string): string {
  return html
    .replace(/<p>/g, '<p style="margin:0;">')
    .replace(/<p style="margin:0;"><\/p>/g, '<p style="margin:0;">&nbsp;</p>')
    .replace(/<p style="margin:0;"><br\s*\/?><\/p>/g, '<p style="margin:0;">&nbsp;</p>');
}
